import { useSEO } from "@/hooks/use-seo";
import { BRAND } from "@/lib/brand-config";
import { SECURITIES_DISCLAIMER_TEXT } from "@/components/securities-disclaimer";

export const TERMS_VERSION = "2026-10";
export const TERMS_EFFECTIVE_DATE = "September 29, 2026";

export default function TermsOfService() {
  useSEO({
    title: "Terms of Service",
    description: `Terms of Service for ${BRAND.siteName}: read about your rights, platform rules, and how we operate.`,
    path: "/terms",
  });

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 prose prose-sm sm:prose dark:prose-invert">
      <h1>Terms of Service</h1>
      <p className="text-sm text-muted-foreground">
        Effective date: {TERMS_EFFECTIVE_DATE} &nbsp;·&nbsp; Version {TERMS_VERSION}
      </p>

      <p>
        Welcome to <strong>{BRAND.siteName}</strong> ("<strong>Veras</strong>", "we", "us", or
        "our"), operated under the domain <em>{BRAND.domain}</em> by {BRAND.legalEntity}. By
        creating an account or using this platform you agree to these Terms of Service
        ("<strong>Terms</strong>"). If you do not agree, do not use the platform.
      </p>

      <h2>1. What Veras Does</h2>
      <p>
        Veras is an educational and deal-facilitation platform focused on the U.S. EB-5
        Immigrant Investor Program. We provide:
      </p>
      <ul>
        <li>Educational articles, guides, and tools about the EB-5 process;</li>
        <li>A curated directory of EB-5 investment project opportunities;</li>
        <li>
          Secure investor dashboards for accreditation, deal-room document access, and
          commitment tracking;
        </li>
        <li>
          Portals for independent partners (including referral / lead-qualifier partners),
          vendor partners (economists, business plan writers), attorneys, and regional center
          sponsors;
        </li>
        <li>Scheduling tools to connect investors with the Veras advisory team.</li>
      </ul>
      <p>
        <strong>Veras is not a registered broker-dealer, investment adviser, or law firm.</strong>{" "}
        Nothing on this platform constitutes investment advice, legal advice, or a solicitation
        to buy or sell securities. Always consult qualified professionals before making any
        investment decision.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years old and legally capable of entering into a binding
        contract in your jurisdiction. By registering you represent that this is true.
      </p>

      <h2>3. Your Account</h2>
      <p>
        You are responsible for keeping your login credentials confidential and for all
        activity that occurs under your account. Notify us immediately at{" "}
        <a href={`mailto:legal@${BRAND.domain}`}>legal@{BRAND.domain}</a> if you believe your
        account has been compromised.
      </p>
      <p>
        We reserve the right to suspend or terminate accounts that violate these Terms, are
        inactive for an extended period, or are used for fraudulent purposes.
      </p>

      <h2>4. Data We Collect and Why</h2>
      <p>To provide the platform we collect:</p>
      <ul>
        <li>
          <strong>Identity data:</strong> your name, email address, phone number, country of
          citizenship, and similar profile information you provide during registration.
        </li>
        <li>
          <strong>Accreditation and financial documents:</strong> files you upload to
          complete the investor accreditation or source-of-funds verification process. These
          are retained for compliance purposes.
        </li>
        <li>
          <strong>Deal-room activity:</strong> records of which offering documents you
          accessed and when, stored as required by securities regulations and our own
          compliance obligations.
        </li>
        <li>
          <strong>Technical data:</strong> your IP address, browser type, device identifiers,
          and referral/UTM parameters used to personalise content and detect fraud.
        </li>
        <li>
          <strong>Communications:</strong> messages you send through the platform, inquiry
          forms, and scheduling requests.
        </li>
      </ul>
      <p>
        We collect this data to operate the platform, verify investor eligibility, comply with
        anti-money-laundering (AML) and know-your-customer (KYC) obligations, facilitate
        introductions to EB-5 projects, and improve the service. We do not sell your personal
        data for money. How we share data with service providers, project parties, and
        independent partners is described in our Privacy Policy (including California
        &ldquo;sell/share&rdquo; definitions that can apply to advertising cookies even when no
        money changes hands).
      </p>

      <h2>5. Document Storage and Security</h2>
      <p>
        Uploaded files (accreditation evidence, subscription documents, etc.) are stored in
        encrypted, access-controlled cloud object storage. Access is gated by your session and
        by server-side authorization checks; we enforce row-level security at the database
        layer so your records are not visible to other users.
      </p>
      <p>
        Despite these precautions, no system is perfectly secure. We cannot guarantee that
        data will never be accessed by unauthorised parties, and you use the platform at your
        own risk.
      </p>

      <h2>6. Investor Accreditation and Deal-Room Access</h2>
      <p>
        Access to private offering materials requires a qualifying investor eligibility
        questionnaire, a project-specific access grant, and acceptance of the applicable
        confidentiality terms. Veras may review eligibility submissions for compliance and
        recordkeeping. That review is not a recommendation to invest.
      </p>
      <p>
        Deal-room documents are confidential and provided for informational purposes only.
        You may not redistribute, share, or reproduce them without the written consent of the
        applicable issuer.
      </p>

      <h2>7. Independent Partners and Vendor Relationships</h2>
      <p>
        Independent partners (including referral and lead-qualifier partners) who introduce
        prospective investors or otherwise participate through the platform are{" "}
        <strong>independent third parties / independent contractors</strong>. They are not
        Veras employees, and use of this platform alone does <strong>not</strong> make any
        partner a broker-dealer, investment adviser, underwriter, placement agent, or finder
        with respect to any securities offering.
      </p>
      <p>
        Where Veras has a separate written partner program, a partner may be compensated under
        that program. Amounts Veras pays under such programs are{" "}
        <strong>Veras operating payables</strong> recorded on Veras&rsquo;s company books —
        they are <strong>not</strong> securities selling commissions, success fees for
        effecting a securities transaction, or deductions from an offering&rsquo;s capital
        raise or administrative fee, unless a separate signed writing expressly says otherwise.
        This site&rsquo;s Terms do not create any partner payment right; partner economics live
        only in those separate written programs.
      </p>
      <p>
        Partners are <strong>not</strong> authorized by these Terms to offer, sell, or solicit
        the purchase of any security, or to bind Veras or any issuer. Project names, economics,
        and offering terms are controlled solely by the formal offering documents and platform
        disclosures Veras (or the applicable issuer) provides.{" "}
        <strong>You should not rely on a partner&rsquo;s oral statements</strong> in place of
        those documents.
      </p>
      <p>
        Veras is not responsible for representations partners make outside the platform or
        outside materials Veras expressly authorizes.
      </p>
      <p>
        Vendors (economists, business-plan writers, fund administrators, and similar service
        providers) contracted by Veras are service providers; their deliverables are subject
        to separate engagement agreements.
      </p>

      <h2>8. Communications and Marketing</h2>
      <p>
        By creating an account you consent to receive transactional emails necessary to
        operate your account (password reset, accreditation status updates, deal-room
        notifications). You may opt out of marketing emails at any time by clicking
        "Unsubscribe" in any marketing message or updating your notification preferences in
        your account settings. Transactional messages cannot be opted out of while your
        account remains active.
      </p>

      <h2>9. Third-Party Services</h2>
      <p>Veras uses third-party services to operate the platform. Material processors include:</p>
      <ul>
        <li>
          <strong>Supabase</strong> — database and user authentication. Your email and hashed
          credentials are stored on Supabase infrastructure.
        </li>
        <li>
          <strong>Cloud object storage</strong> — secure file storage for documents and
          images.
        </li>
        <li>
          <strong>Resend</strong> — transactional and notification email.
        </li>
        <li>
          <strong>Customer.io</strong> — marketing automation and email/sequence delivery for
          users who have consented to marketing (or as otherwise permitted by law).
        </li>
        <li>
          <strong>OpenAI</strong> — optional AI-assisted features such as document
          summarisation and the investor onboarding assistant. No identifying information is
          sent to OpenAI models without your explicit interaction with those features.
        </li>
        <li>
          <strong>Sentry</strong> — error monitoring. Crash reports may contain sanitised
          request metadata; we configure Sentry to mask sensitive fields.
        </li>
        <li>
          <strong>Google Calendar API</strong> — calendar integration for scheduling
          consultations (only if you authorise it).
        </li>
      </ul>
      <p>
        We also use other service providers who need the data to perform their contracted job
        (for example analytics tooling gated by cookie consent). Each provider&rsquo;s own
        privacy policy governs their handling of data shared with them. See the Privacy Policy
        for categories of recipients and how to manage marketing and cookie preferences.
      </p>

      <h2>10. Intellectual Property</h2>
      <p>
        All content on the platform (articles, guides, design elements, software) is owned by
        Veras or its licensors. You may not reproduce, scrape, or create derivative works
        without written permission.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, Veras and its officers, directors, employees,
        and agents shall not be liable for any indirect, incidental, special, consequential,
        or punitive damages arising from your use of (or inability to use) the platform, even
        if we have been advised of the possibility of such damages. Our aggregate liability to
        you for any claim arising out of these Terms shall not exceed the amount you paid us
        (if any) in the twelve months preceding the claim.
      </p>

      <h2>12. Disclaimer of Warranties</h2>
      <p>
        The platform is provided "as is" and "as available" without warranties of any kind,
        express or implied, including but not limited to merchantability, fitness for a
        particular purpose, and non-infringement.
      </p>

      <h2>13. User Rights and Data Requests</h2>
      <p>
        Depending on your jurisdiction you may have the right to access, correct, or delete
        your personal data. To make a request, email{" "}
        <a href={`mailto:privacy@${BRAND.domain}`}>privacy@{BRAND.domain}</a>. We aim to
        respond within 30 days (or sooner if required by law; California requests generally
        within 45 days). Note that certain records (e.g. accreditation evidence, document
        access logs) may need to be retained for regulatory purposes even after a deletion
        request. Additional California / U.S. state privacy rights are described in our
        Privacy Policy.
      </p>

      <h2>14. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of{" "}
        <strong>South Carolina</strong>, United States, without regard to conflict-of-law
        principles. Any dispute arising out of or relating to these Terms or the platform
        shall be resolved exclusively in the state or federal courts located in{" "}
        <strong>Greenville County, South Carolina</strong>, and you consent to personal
        jurisdiction and venue there.
      </p>

      <h2>15. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. We will notify you of material changes by
        email or by displaying a notice in the platform. Continued use of the platform after
        the effective date of revised Terms constitutes your acceptance of the changes. When
        we publish a new document version, logged-in users may be required to re-accept before
        continuing to use certain features.
      </p>

      <h2>16. Securities Disclaimer</h2>
      <p data-testid="text-terms-securities-disclaimer">{SECURITIES_DISCLAIMER_TEXT}</p>

      <h2>17. Contact Us</h2>
      <p>
        Questions about these Terms? Email us at{" "}
        <a href={`mailto:legal@${BRAND.domain}`}>legal@{BRAND.domain}</a>.
      </p>
      <p>Mailing address: {BRAND.mailingAddress}.</p>

      <hr />
      <p className="text-xs text-muted-foreground">
        Document version {TERMS_VERSION} · Last updated {TERMS_EFFECTIVE_DATE} · Supersedes
        2026-05 / May 1, 2026
      </p>
    </div>
  );
}
